import { Sparkles } from 'lucide-react'

export interface ReasonTemplate {
  id: string
  label: string
  body: string
  /** When true, picking the template auto-flips `is_regulatory` on the form. */
  regulatory?: boolean
}

const TEMPLATES: ReadonlyArray<ReasonTemplate> = [
  {
    id: 'fraud',
    label: 'Suspected fraud',
    body: 'Suspected fraud — multiple alerts indicate mule-account activity. Holding pending review.',
  },
  {
    id: 'nfiu',
    label: 'NFIU directive',
    body: 'NFIU directive [REF-XXXX]: regulatory hold pending NFIU response. Do not lift without compliance sign-off.',
    regulatory: true,
  },
  {
    id: 'court',
    label: 'Court order',
    body: 'Court order [CASE-XXXX]: subject to ongoing legal proceedings. Compliance team owns lift workflow.',
    regulatory: true,
  },
  {
    id: 'pnd',
    label: 'Customer PND request',
    body: 'Customer-requested PND. Verified via inbound branch ticket; lift requires customer re-confirmation.',
  },
  {
    id: 'ato',
    label: 'Account takeover',
    body: 'Suspected account takeover — credential reuse + new device fingerprint with high-value outbound.',
  },
  {
    id: 'compromise',
    label: 'Compromised credentials',
    body: 'Confirmed credential compromise via partner threat-intel feed. Force re-authentication on lift.',
  },
]

interface Props {
  onPick: (template: ReasonTemplate) => void
}

/**
 * AF-128 — chip-row of reason templates. Click → fill the textarea.
 * The AC mandates these speed up the most common request paths so
 * analysts don't free-form a regulatory hold and forget the directive ref.
 */
export function ReasonTemplates({ onPick }: Props) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-fg">
        <Sparkles size={10} aria-hidden /> Templates
      </p>
      <div className="flex flex-wrap gap-1.5">
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onPick(t)}
            className="rounded-sm border border-border bg-card px-2 py-0.5 text-xs hover:border-primary/40 hover:bg-primary/5"
          >
            {t.label}
            {t.regulatory && (
              <span className="ml-1 text-[0.625rem] uppercase text-priority-critical">
                · reg
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

export type { ReasonTemplate as TemplateOption }
